import React, { useCallback, useEffect, useState } from 'react';
import { Form, message } from 'antd';
import type { FormInstance } from 'antd/es/form';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useDebounce } from 'use-debounce';
import { useLocation, useParams } from 'react-router-dom';

export interface DataType {
    key: React.Key;
    itemInvoice: string;
    partNoInvoice: string;
    itemPO: string;
    partNoPO: string;
}

export interface SalesMasterData {
    se_no: string;
    so_no: string;
    sales_date: string;
    sales_person: string;
}

export interface SalesDetailDataType {
    id: number;
    item_code: string;
    item_name: string;
    dmd: string;
}

export type AutosaveStatus = 'idle' | 'processing' | 'error' | 'stopped';

export interface WithEditableTableProps {
    dataSource: DataType[];
    editingKey: React.Key;
    form: FormInstance<any>;
    handleSave: () => void;
    handleDelete: (key: React.Key) => void;
    handleAdd: () => void;
    handleCellClick: (record: DataType) => void;
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement>, key: React.Key, dataIndex: keyof DataType) => void;
    handleCustomerNameChange: () => void;
    isEditing: (record: DataType) => boolean;
    isModalOpen: boolean;
    salesMasterData: SalesMasterData[];
    handleOpenModal: () => void;
    handleCloseModal: () => void;
    handleImport: (selectedItems: Pick<SalesDetailDataType, 'item_code' | 'item_name'>[]) => void;
    autosaveStatus: AutosaveStatus;
    handleSubmit: () => void;
    isFormDisabled: boolean;
    handlePrint: () => void;
}


const withEditableTable = <P extends object>(WrappedComponent: React.ComponentType<P & WithEditableTableProps>) => {
    const EnhancedComponent: React.FC<P> = (props) => {
        const [dataSource, setDataSource] = useState<DataType[]>([]);
        const [count, setCount] = useState(0);
        const [editingKey, setEditingKey] = useState<React.Key>('');
        const [form] = Form.useForm();
        const [isModalOpen, setIsModalOpen] = useState(false);
        const [salesMasterData, setSalesMasterData] = useState<SalesMasterData[]>([]);
        const { userData } = useAuth();
        const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>('stopped');
        const [isAutosaveActive, setIsAutosaveActive] = useState(false);
        const [isFormDisabled, setIsFormDisabled] = useState(false);
        const allFormData = Form.useWatch([], form);
        const [debouncedDataSource] = useDebounce(dataSource, 1500);
        const [debouncedAllFormData] = useDebounce(allFormData, 1500);
        const location = useLocation();
        const no_subs_from_state = location.state?.no_subs;

        useEffect(() => {
            if (userData) {
                form.setFieldsValue({ sales: userData.nama });
            }
        }, [userData, form]);

        useEffect(() => {
            const fetchDataForEdit = async (subsId: string) => {
                try {
                    const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/substitution/get-subs/${encodeURIComponent(subsId)}`);
                    const { master, details } = response.data;

                    const isDraft = master.status === '1';
                    form.setFieldsValue({
                        status: isDraft ? 'Draft' : 'Approve',
                        no_subs: master.no_subs,
                        date: master.date,
                        customer_name: master.cs_name,
                        no_po: master.no_po,
                        sales: master.sales,
                    });

                    const formattedDetails: DataType[] = details.map((d: any, index: number) => ({
                        key: index,
                        itemInvoice: d.item_invoice,
                        partNoInvoice: d.part_no_invoice,
                        itemPO: d.item_po,
                        partNoPO: d.part_no_po,
                    }));
                    setDataSource(formattedDetails);
                    setCount(details.length);

                    if (isDraft) {
                        setIsAutosaveActive(true);
                        setAutosaveStatus('idle');
                    } else {
                        setIsFormDisabled(true);
                        setIsAutosaveActive(false);
                        setAutosaveStatus('stopped');
                    }

                } catch (error) {
                    console.error("Gagal memuat data substitusi:", error);
                    message.error("Gagal memuat data substitusi untuk diedit.");
                }
            };

            if (no_subs_from_state) {
                fetchDataForEdit(no_subs_from_state);
            }
        }, [no_subs_from_state, form]);

        const triggerAutosave = useCallback(async () => {
            const formData = form.getFieldsValue();
            if (!isAutosaveActive || formData.status !== 'Draft') {
                setAutosaveStatus('stopped');
                if (isAutosaveActive) setIsAutosaveActive(false);
                return;
            }

            if (!formData.no_subs || !formData.customer_name) {
                setAutosaveStatus('stopped');
                return;
            }

            setAutosaveStatus('processing');

            const masterData = {
                status: formData.status === 'Draft' ? '1' : formData.status,
                no_subs: formData.no_subs,
                date: formData.date,
                cs_name: formData.customer_name,
                no_po: formData.no_po,
                sales: formData.sales,
            };

            const detailData = dataSource.map(item => ({
                no_subs: formData.no_subs,
                item_invoice: item.itemInvoice,
                part_no_invoice: item.partNoInvoice,
                item_po: item.itemPO,
                part_no_po: item.partNoPO,
            }));

            try {
                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/substitution/save`, {
                    master: masterData,
                    details: detailData,
                });
                setAutosaveStatus('idle');
            } catch (error) {
                console.error("Autosave failed:", error);
                message.error("Gagal menyimpan data secara otomatis.");
                setAutosaveStatus('error');
            }
        }, [isAutosaveActive, dataSource, form]);

        useEffect(() => {
            if (isAutosaveActive) {
                triggerAutosave();
            }
        }, [debouncedDataSource, debouncedAllFormData, isAutosaveActive, triggerAutosave]);

        const isEditing = (record: DataType) => record.key === editingKey;

        const handleAdd = () => {
            const newData: DataType = {
                key: count,
                itemInvoice: ``,
                partNoInvoice: ``,
                itemPO: ``,
                partNoPO: ``,
            };
            setDataSource([...dataSource, newData]);
            setCount(count + 1);
        };

        const handleDelete = (key: React.Key) => {
            const newData = dataSource.filter((item) => item.key !== key);
            setDataSource(newData);
        };

        const handleSave = () => {
            setEditingKey('');
        };

        const handleCellClick = (record: DataType) => {
            if (editingKey === '') {
                setEditingKey(record.key);
            }
        };

        const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, key: React.Key, dataIndex: keyof DataType) => {
            const newDataSource = dataSource.map(item => {
                if (item.key === key) {
                    return { ...item, [dataIndex]: e.target.value };
                }
                return item;
            });
            setDataSource(newDataSource);
        };

        const handleCustomerNameChange = async () => {
            try {
                const noSubsValue = form.getFieldValue('no_subs');
                if (!noSubsValue) {
                    const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/substitution/generate-no-subs`);
                    if (response.data && response.data.no_subs) {
                        form.setFieldsValue({ no_subs: response.data.no_subs, date: response.data.date });
                        setIsAutosaveActive(true);
                        setAutosaveStatus('idle');
                    }
                }
                else if (!isAutosaveActive) {
                    setIsAutosaveActive(true);
                    setAutosaveStatus('idle');
                }
            } catch (error) {
                console.error("Failed to generate substitution number:", error);
                message.error("Gagal menghasilkan nomor substitusi.");
            }
        };

        const fetchSalesMasterData = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/substitution/ssales-master-subs`);
                setSalesMasterData(response.data);
            } catch (error) {
                console.error("Failed to fetch sales master data:", error);
                message.error("Gagal mengambil data SO.");
            }
        };

        const handleOpenModal = () => {
            fetchSalesMasterData();
            setIsModalOpen(true);
        };

        const handleCloseModal = () => {
            setIsModalOpen(false);
        };

        const handleImport = (selectedItems: Pick<SalesDetailDataType, 'item_code' | 'item_name'>[]) => {
            const newItems: DataType[] = selectedItems.map((item, index) => ({
                key: count + index,
                itemInvoice: item.item_name,
                partNoInvoice: item.item_code,
                itemPO: '',
                partNoPO: '',
            }));

            setDataSource(prevDataSource => [...prevDataSource, ...newItems]);
            setCount(prevCount => prevCount + newItems.length);
            message.success(`${newItems.length} item berhasil diimpor.`);
        };

        const handleSubmit = async () => {
            const no_subs = form.getFieldValue('no_subs');
            if (!no_subs) {
                message.error('Tidak ada No. Subs untuk disubmit. Harap isi data terlebih dahulu.');
                return;
            }
            if (form.getFieldValue('status') !== 'Draft') {
                message.warning('Data sudah berstatus Approve.');
                return;
            }

            try {
                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/substitution/submit`, { no_subs });

                form.setFieldsValue({ status: 'Approve' });
                setIsAutosaveActive(false);
                setAutosaveStatus('stopped');
                setIsFormDisabled(true);
                setEditingKey('');
                message.success('Data berhasil disubmit dan sekarang dalam mode read-only.');

            } catch (error) {
                console.error("Gagal submit data:", error);
                message.error("Gagal submit data. Silakan coba lagi.");
            }
        };

        const handlePrint = async () => {
            const no_subs = form.getFieldValue('no_subs');
            if (!no_subs) {
                message.error('Tidak ada No. Subs untuk dicetak. Harap simpan data terlebih dahulu.');
                return;
            }

            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/substitution/get-subs/${encodeURIComponent(no_subs)}`);
                const { master, details } = response.data;

                const reportUrl = `${process.env.PUBLIC_URL}/reports/surat_substitusi/index.html`;
                const reportResponse = await fetch(reportUrl);
                let templateHtml = await reportResponse.text();

                const baseUrl = `${process.env.PUBLIC_URL}/reports/surat_substitusi/`;
                templateHtml = templateHtml.replace(/(href|src)="(?!https?:\/\/)([^"]+)"/g, `$1="${baseUrl}$2"`);

                const today = new Date();
                const months = ['Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni', 'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'];
                const formattedDate = `Sampit, ${today.getDate()} ${months[today.getMonth()]} ${today.getFullYear()}`;

                templateHtml = templateHtml.replace('fields_tanggal', formattedDate);
                templateHtml = templateHtml.replace('fields_no', master.no_subs || '');
                templateHtml = templateHtml.replace('fields_customer', master.cs_name || '');
                templateHtml = templateHtml.replace('fields_po', master.no_po || '');
                templateHtml = templateHtml.replace('fields_sales', master.sales || '');

                const tableRowsHtml = details.map((item: any, index: number) => `
                            <tr style="margin-top: 1px; padding-top: 1px; padding-bottom: 2px; margin-bottom: 2px;">
                                <td style="padding-top: 2px; padding-bottom: 2px;">${index + 1}</td>
                                <td style="padding-top: 2px; padding-bottom: 2px;">${item.item_invoice || ''}</td>
                                <td style="padding-top: 2px; padding-bottom: 2px; border-right: 1px solid black;">${item.part_no_invoice || ''}</td>
                                <td style="padding-top: 2px; padding-bottom: 2px;">${item.item_po || ''}</td>
                                <td style="padding-top: 2px; padding-bottom: 2px;">${item.part_no_po || ''}</td>
                            </tr>
                        `).join('');

                templateHtml = templateHtml.replace(/<tbody>[\s\S]*?<\/tbody>/, `<tbody>${tableRowsHtml}</tbody>`);

                const printWindow = window.open('', '_blank');
                if (printWindow) {
                    printWindow.document.write(templateHtml);
                    printWindow.document.close();
                    printWindow.focus();
                    setTimeout(() => {
                        printWindow.print();
                    }, 500);
                } else {
                    message.error('Gagal membuka jendela cetak. Mohon izinkan pop-up untuk situs ini.');
                }
            } catch (error) {
                console.error("Gagal mencetak data:", error);
                message.error("Gagal mengambil data untuk dicetak. Silakan coba lagi.");
            }
        };

        return (
            <WrappedComponent
                {...props as P}
                dataSource={dataSource}
                editingKey={editingKey}
                form={form}
                handleSave={handleSave}
                handleDelete={handleDelete}
                handleAdd={handleAdd}
                handleCellClick={handleCellClick}
                handleInputChange={handleInputChange}
                handleCustomerNameChange={handleCustomerNameChange}
                isEditing={isEditing}
                isModalOpen={isModalOpen}
                salesMasterData={salesMasterData}
                handleOpenModal={handleOpenModal}
                handleCloseModal={handleCloseModal}
                handleImport={handleImport}
                autosaveStatus={autosaveStatus}
                handleSubmit={handleSubmit}
                isFormDisabled={isFormDisabled}
                handlePrint={handlePrint}
            />
        );
    };

    return EnhancedComponent;
};

export default withEditableTable;